import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { getDecisionMap, saveDecisionMap } from '../../services/mailIntelApi';
import { roleLabel } from './MailThreadPanel';
import { Btn, DetailShell, ErrorBanner, Spinner, T, humanise } from './shared';

const typeLabel = (type) => {
  const text = humanise(type);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Decision owners: which delivery role makes each kind of decision a mail
 * asks for, on this project. Changes apply to old mail too (triage re-reads
 * the map). Read-only unless the backend says `can_edit`.
 */
const DecisionMapPanel = ({ accessToken, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getDecisionMap(accessToken);
      setData(res?.data || null);
      setDraft(res?.data?.map || {});
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data && !error) return <Spinner />;

  const changed =
    !!data && data.decision_types.some((t) => draft[t] !== data.map[t]);

  const save = async () => {
    // Send only what differs from the shipped default.
    const overrides = Object.fromEntries(
      data.decision_types
        .filter((t) => draft[t] !== data.defaults[t])
        .map((t) => [t, draft[t]]),
    );
    setSaving(true);
    try {
      const res = await saveDecisionMap(accessToken, overrides, data.version);
      setData({ ...data, ...res?.data });
      setDraft(res?.data?.map || draft);
      toast.success('Decision owners saved');
      onSaved?.();
    } catch (saveError) {
      const message = String(saveError?.message || saveError);
      if (message.includes('reload')) {
        toast.error('Someone else changed this first. Showing their version.');
        await load();
      } else {
        toast.error(`Could not save: ${message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailShell
      eyebrow="Mail triage"
      title="Decision owners"
      onClose={onClose}
      footer={
        data?.can_edit && (
          <div className="flex justify-end gap-2">
            <Btn
              variant="ghost"
              onClick={() => setDraft(data.map)}
              disabled={!changed || saving}
            >
              Undo changes
            </Btn>
            <Btn variant="primary" onClick={save} disabled={!changed || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        )
      }
    >
      <ErrorBanner>{error}</ErrorBanner>
      {data && (
        <>
          <p className="mb-4 text-[13px] text-zinc-400">
            When a mail asks for a decision, the role below owns it: their inbox
            shows it under “Needs your decision”. This changes ranking only,
            never who can see the mail.
            {!data.can_edit && ' Only a project PM can change it.'}
          </p>
          <div className="flex flex-col divide-y divide-zinc-800 rounded-xl border border-zinc-700/60 bg-zinc-800/30">
            {data.decision_types.map((type) => {
              const isDefault = draft[type] === data.defaults[type];
              return (
                <div
                  key={type}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <span className="text-[13px] text-zinc-100">
                    {typeLabel(type)}
                  </span>
                  {data.can_edit ? (
                    <select
                      aria-label={`Owner of ${typeLabel(type)}`}
                      value={draft[type]}
                      onChange={(e) =>
                        setDraft({ ...draft, [type]: e.target.value })
                      }
                      className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-[#5652CF]"
                    >
                      {data.roles.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r)}
                          {r === data.defaults[type] ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="text-[13px] font-semibold"
                      style={{ color: isDefault ? T.textSec : T.decision }}
                    >
                      {roleLabel(draft[type])}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-zinc-500">
            Version {data.version || 'default'}
          </p>
        </>
      )}
    </DetailShell>
  );
};

export default DecisionMapPanel;
